import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import UiKitButton from 'components/UiKit/Button';
import SuccessIcon from 'components/Icons/components/SuccessIcon';
import WarningIcon from 'components/Icons/components/WarningIcon';
import ErrorIcon from 'assets/error-circle-outline.svg?react';
import CloseIcon from 'assets/close_18px.svg?react';

import './index.css';

const toastDefaultPropOnClose = () => {};
const toastDefaultPropCustomStyle = {};

// The design's 14px/20px body token carries this tracking. It is load-bearing
// here, not cosmetic: without it the first sentence of a two-line message
// overflows its line and breaks a word early.
const BODY_TRACKING = '-0.105px';
const Toast = ({
  type = 'success',
  title = '',
  onClose = toastDefaultPropOnClose,
  open = false,
  timeout = 5000,
  children = null,
  isBold = false,
  customStyle = toastDefaultPropCustomStyle
}) => {
  const [showToast, setShowToast] = useState(open);

  useEffect(() => {
    setShowToast(open);
  }, [open]);

  useEffect(() => {
    let timer;
    if (showToast && timeout >= 0) {
      timer = setTimeout(() => {
        setShowToast(false);
        onClose();
      }, timeout);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [showToast, timeout, onClose]);

  const ToastIcon = {
    success: <SuccessIcon width={24} height={24} />,
    warning: <WarningIcon />,
    info: (
      <svg aria-hidden="true" width="24" height="24" viewBox="0 0 20 20">
        <path
          fill="#0092E4"
          d="M20 10C20 15.5241 15.5225 20 10 20C4.47754 20 0 15.5241 0 10C0 4.47915 4.47754 0 10 0C15.5225 0 20 4.47915 20 10ZM10 12.0161C8.97561 12.0161 8.14516 12.8466 8.14516 13.871C8.14516 14.8954 8.97561 15.7258 10 15.7258C11.0244 15.7258 11.8548 14.8954 11.8548 13.871C11.8548 12.8466 11.0244 12.0161 10 12.0161ZM8.23899 5.34895L8.5381 10.8328C8.5521 11.0894 8.76427 11.2903 9.02125 11.2903H10.9788C11.2357 11.2903 11.4479 11.0894 11.4619 10.8328L11.761 5.34895C11.7761 5.07177 11.5554 4.83871 11.2779 4.83871H8.7221C8.44452 4.83871 8.22387 5.07177 8.23899 5.34895Z"
        />
      </svg>
    ),
    error: <ErrorIcon />
  };

  const borderColor = {
    success: '#19A04B',
    warning: '#ECC94B',
    info: '#0092E4',
    error: '#CF3537'
  };

  return (
    <div
      className={` toast fixed transition-all duration-500 ease-in top-9 ${showToast &&
        'show-toast'} py-4 px-6 bg-white rounded flex ${
        children ? 'w-2/6' : 'w-1/4'
      }`}
      style={{
        zIndex: 2000,
        // Drawn as an inset shadow, the way the design does, so the status bar
        // sits over the padding instead of taking 4px out of the content box.
        boxShadow: `inset 4px 0 0 -1px ${borderColor[type]}, 0 2px 3px 3px rgba(0, 0, 0, 0.2)`,
        ...customStyle
      }}
      data-testid="toast-alert"
    >
      <div className="w-full flex">
        <div className="mr-4">{ToastIcon[type]}</div>
        <div className="w-full">
          <div className="flex justify-between font-body items-center">
            <h4
              className={`text-base capitalize-first ${
                isBold ? 'font-semibold' : ''
              }`}
            >
              {title}
            </h4>
            <UiKitButton
              aria-label="alert-close"
              variant="text"
              onClick={onClose}
              icon={<CloseIcon aria-hidden="true" />}
            />
          </div>
          {children && (
            <div
              className="mt-2 text-sm leading-5"
              style={{ letterSpacing: BODY_TRACKING }}
            >
              {children}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

Toast.propTypes = {
  type: PropTypes.oneOf(['success', 'warning', 'error', 'info']),
  onClose: PropTypes.func,
  title: PropTypes.string,
  children: PropTypes.node,
  open: PropTypes.bool,
  timeout: PropTypes.number,
  isBold: PropTypes.bool,
  // eslint-disable-next-line react/forbid-prop-types
  customStyle: PropTypes.object
};

export default Toast;
